'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, IcArrowDown, IcLikeFilled, Image, LikeMotion, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { LikedSnapsCountMap, VisitorCurationSnapItem } from '@musinsa/services-curator';
import { ERROR_CODE_LIKE_UNAUTHORIZED, isApiError } from '@musinsa/services-curator';
import { useMemo, useState } from 'react';
import { getPaths } from '../../common';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { useLikedSnapsCountQuery } from '../../common/hooks/useLikedSnapsCountQuery';
import { useNavigate } from '../../common/hooks/useNavigate';
import { useSnapLikeMutation } from '../../common/hooks/useSnapLikeMutation';

interface Props {
  snsTitle: string;
  curationId: string;
  items: VisitorCurationSnapItem[];
  className?: string;
}

const MIN_COUNT = 9;

interface VisitorSnapTileProps {
  snsTitle: string;
  curationId: string;
  snap: VisitorCurationSnapItem;
  index: number;
  isLiked: boolean;
}

const VisitorSnapTile = ({ snsTitle, curationId, snap, index, isLiked }: VisitorSnapTileProps) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const { navigate } = useNavigate();
  const paths = getPaths(service);

  const snapLikeMutation = useSnapLikeMutation({
    onError: (error) => {
      if (isApiError(error) && error.data?.meta?.errorCode === ERROR_CODE_LIKE_UNAUTHORIZED) {
        const url = createLoginUrl(window.location.href);
        navigate(url);
      }
    },
  });

  const onClickSnap = () => {
    if (isMusinsa) {
      amplitudeSendEvent({
        action_name: 'click',
        component_name: 'snap',
        event_name: 'click_snap',
        from: getScreenName(),
        id: snsTitle,
        index,
        name: snap.id,
        section_name: 'curator_curationfeed',
        type: 'snap',
      });
    }
    navigate(
      paths.snapDetail({
        curationId,
        query: { snapId: snap.id, shopSlug: snsTitle },
      }),
      { newWebview: true },
    );
  };

  const handleClickLike = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    const next = !isLiked;
    snapLikeMutation.mutate({ snapId: snap.id, isLiked: next });

    if (isMusinsa) {
      amplitudeSendEvent({
        action_name: next ? 'like_snap' : 'unlike_snap',
        component_name: 'snap',
        event_name: next ? 'like_snap_snap' : 'unlike_snap_snap',
        from: getScreenName(),
        id: snsTitle,
        index,
        name: snap.id,
        section_name: 'curator_curationfeed',
        type: 'snap',
      });
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="relative aspect-[5/6] overflow-hidden cursor-pointer"
      onClick={onClickSnap}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClickSnap();
        }
      }}
    >
      <Image src={snap.imageUrl} alt="" className="w-full h-full object-cover" />
      <div className="absolute right-1 bottom-1 z-10 inline-flex">
        <LikeMotion like={isLiked}>
          {isLiked ? (
            <IcLikeFilled
              className="cursor-pointer"
              fillColor="red"
              strokeColor="transparent"
              width="20px"
              height="20px"
              onClick={handleClickLike}
            />
          ) : (
            <IcLikeFilled
              className="cursor-pointer"
              fillColor="gray-500"
              strokeColor="white"
              opacity={0.6}
              width="20px"
              height="20px"
              onClick={handleClickLike}
            />
          )}
        </LikeMotion>
      </div>
    </div>
  );
};

export const VisitorSnapItems = ({ snsTitle, curationId, items, className }: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;

  const snapIds = useMemo(() => items.map((item) => item.id), [items]);
  const { data: likedMap } = useLikedSnapsCountQuery(snapIds, { enabled: isMusinsa });
  const likedSnapsMap: LikedSnapsCountMap = likedMap ?? {};

  const showMore = useMemo(
    () => items.length > MIN_COUNT * currentPage,
    [items.length, currentPage],
  );

  if (items.length === 0) return null;

  const onLoadMore = () => {
    if (isMusinsa) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curation_more',
        component_name: 'more_button',
        event_name: 'click_more_button',
        from: getScreenName(),
        id: snsTitle,
        section_name: 'curator_curationfeed',
        type: 'snap',
      });
    }
    setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className={className}>
      <div className="grid w-full grid-cols-3 bg-neutral-color-white">
        {items.slice(0, MIN_COUNT * currentPage).map((snap, index) => (
          <VisitorSnapTile
            key={snap.id}
            snsTitle={snsTitle}
            curationId={curationId}
            snap={snap}
            index={index}
            isLiked={likedSnapsMap[snap.id]?.isLiked ?? false}
          />
        ))}
      </div>
      {showMore && (
        <div className="flex justify-center px-4 py-3">
          <Button
            className="flex w-full items-center gap-[2px]"
            size={40}
            variant="tertiaryOutlinedGray"
            onClick={onLoadMore}
          >
            <Typography variant="body14pxMed">
              더보기({MIN_COUNT * currentPage}/{items.length}개)
            </Typography>
            <IcArrowDown height="16px" width="16px" />
          </Button>
        </div>
      )}
    </div>
  );
};
